'use client';
'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
require('../../DataDisplays/Avatar/Avatar.js');
var CountBadge = require('../../DataDisplays/CountBadge/CountBadge.js');
require('../../DataDisplays/Label/Label.js');
var SystemIcon = require('../../DataDisplays/SystemIcon/SystemIcon.js');

const TextTabContext = react.createContext({
    barColor: 'bg-w-gray-900',
    activeTab: 0,
    currentActiveTab: 0,
    isFit: true,
    hasPadding: false,
    isFull: false,
    handleTabClick: (index)=>{}
});
/**
 * TextTab의 Root 컴포넌트입니다.
 * TextTab의 상태를 관리합니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 자식 컴포넌트
 * @param {number} [props.currentActiveTab=0] - 현재 활성화된 탭의 인덱스
 * @param {boolean} [props.isFit=true] - 탭의 너비를 컨텐츠에 맞출지 여부
 * @param {boolean} [props.hasPadding=false] - 좌우 패딩 적용 여부
 * @param {boolean} [props.isFull=false] - 탭이 전체 너비를 차지할지 여부
 * @param {string} [props.barColor='bg-w-gray-900'] - 선택 표시줄의 색상
 * @param {(index: number) => void} [props.onChange] - 탭 변경 시 호출되는 콜백 함수
 *
 * @example
 * <TextTab.Root currentActiveTab={0} onChange={(index) => console.log(index)}>
 *   <TextTab.Tabs>
 *     <TextTab.Tab index={0}>첫 번째 탭</TextTab.Tab>
 *     <TextTab.Tab index={1}>두 번째 탭</TextTab.Tab>
 *   </TextTab.Tabs>
 *   <TextTab.Panel index={0}>첫 번째 패널 내용</TextTab.Panel>
 *   <TextTab.Panel index={1}>두 번째 패널 내용</TextTab.Panel>
 * </TextTab.Root>
 */ const Root = ({ children, currentActiveTab = 0, isFit = true, hasPadding = false, isFull = false, barColor = 'bg-w-gray-900', onChange })=>{
    const [activeTab, setActiveTab] = react.useState(currentActiveTab);
    const handleTabClick = (index)=>{
        onChange === null || onChange === void 0 ? void 0 : onChange(index);
        setActiveTab(index);
    };
    react.useEffect(()=>{
        setActiveTab(currentActiveTab);
    }, [
        currentActiveTab
    ]);
    return jsxRuntime.jsx(TextTabContext.Provider, {
        value: {
            barColor,
            activeTab,
            currentActiveTab,
            isFit,
            hasPadding,
            handleTabClick,
            isFull
        },
        children: children
    });
};
/**
 * 탭 목록을 감싸는 컨테이너 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 탭 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * <TextTab.Tabs>
 *   <TextTab.Tab index={0}>첫 번째 탭</TextTab.Tab>
 *   <TextTab.Tab index={1}>두 번째 탭</TextTab.Tab>
 * </TextTab.Tabs>
 */ const Tabs = ({ children, className })=>{
    const { activeTab, hasPadding, isFull, barColor } = react.useContext(TextTabContext);
    const tabRefs = react.useRef([]);
    const underbarRef = react.useRef(null);
    const childrenArray = react.Children.toArray(children);
    react.useEffect(()=>{
        if (!tabRefs.current[activeTab] || !underbarRef.current) return;
        const activeTabElement = tabRefs.current[activeTab];
        const underbarElement = underbarRef.current;
        const containerElement = underbarElement.parentElement;
        if (activeTabElement && underbarElement && containerElement) {
            const { offsetLeft, offsetWidth } = activeTabElement;
            const { scrollLeft, clientWidth } = containerElement;
            underbarElement.style.width = `${offsetWidth}px`;
            underbarElement.style.transform = `translateX(${offsetLeft}px)`;
            requestAnimationFrame(()=>{
                const isVisible = scrollLeft <= offsetLeft && scrollLeft + clientWidth >= offsetLeft + offsetWidth;
                if (!isVisible) {
                    activeTabElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'center'
                    });
                }
            });
        }
    }, [
        activeTab
    ]);
    return jsxRuntime.jsxs(Box, {
        className: tailwindMerge.twJoin('relative block w-full items-start overflow-x-scroll border-b border-w-gray-200 scrollbar-hide', hasPadding && 'px-9', className),
        "data-testid": "design-system-textTab--container",
        children: [
            jsxRuntime.jsx(Box, {
                className: tailwindMerge.twMerge('inline-flex w-max gap-10', isFull && 'w-full gap-0'),
                children: childrenArray.map((child, index)=>{
                    return jsxRuntime.jsx(Box, {
                        className: tailwindMerge.twMerge(isFull && 'w-full'),
                        ref: (el)=>tabRefs.current[index] = el,
                        children: child
                    }, index);
                })
            }),
            jsxRuntime.jsx("span", {
                ref: underbarRef,
                "data-testid": `design-system-textTab--underbar-${activeTab}`,
                className: tailwindMerge.twJoin('absolute bottom-0 left-0 h-0.5 rounded-t-lg transition-all duration-300 ease-out', barColor)
            })
        ]
    });
};
/**
 * 개별 탭 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 탭의 레이블
 * @param {number} props.index - 탭의 인덱스
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {string} [props.barColor] - 선택 표시줄의 색상
 * @param {boolean} [props.disabled=false] - 탭 비활성화 여부
 * @param {number} [props.badgeNumber] - 뱃지에 표시될 숫자
 * @param {string} [props.iconName] - 탭에 표시될 아이콘 이름
 *
 * @example
 * <TextTab.Tab
 *   index={0}
 *   iconName="home"
 *   badgeNumber={5}
 * >
 *   홈
 * </TextTab.Tab>
 */ const Tab = ({ children, index, className, disabled = false, badgeNumber, iconName })=>{
    const { isFit, handleTabClick, activeTab, isFull } = react.useContext(TextTabContext);
    const isSelected = index === activeTab;
    return jsxRuntime.jsx(Box, {
        tabIndex: 0,
        "data-testid": `design-system-textTab--item-${index}`,
        className: tailwindMerge.twMerge('group flex w-max cursor-pointer items-center rounded-xl hover:font-medium', disabled && 'cursor-not-allowed', isFull && `w-full`, className),
        onClick: ()=>{
            !disabled && handleTabClick(index);
        },
        children: jsxRuntime.jsxs(Box, {
            className: tailwindMerge.twMerge('relative flex w-full flex-row items-center justify-center gap-2 py-4 font-normal text-w-gray-600 group-hover:text-w-gray-900', !isFit && 'px-[33.5px]', isFull && 'w-full px-0', isSelected && 'font-medium text-w-gray-900'),
            children: [
                iconName && jsxRuntime.jsx(Box, {
                    className: "flex size-4 flex-shrink-0 items-center justify-center",
                    children: jsxRuntime.jsx(SystemIcon, {
                        name: iconName,
                        className: tailwindMerge.twMerge('size-4 text-w-gray-600 group-hover:text-w-gray-900', isSelected && 'text-w-gray-900')
                    })
                }),
                jsxRuntime.jsx(Typography, {
                    variant: "body1",
                    className: "relative",
                    children: children
                }),
                badgeNumber !== undefined && jsxRuntime.jsx(CountBadge, {
                    text: badgeNumber,
                    showZero: true,
                    className: "relative",
                    variant: "gray"
                })
            ]
        })
    });
};
/**
 * 탭 패널 컴포넌트입니다.
 * 해당 탭이 선택되었을 때만 내용을 표시합니다.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 패널의 내용
 * @param {number} props.index - 패널의 인덱스 (연결된 탭의 인덱스와 일치해야 함)
 *
 * @example
 * <TextTab.Panel index={0}>
 *   첫 번째 탭의 내용입니다.
 * </TextTab.Panel>
 */ const Panel = ({ children, index })=>{
    const { activeTab } = react.useContext(TextTabContext);
    return activeTab === index && jsxRuntime.jsx(Box, {
        "data-testid": "design-system-textTab--content",
        children: children
    });
};

exports.Panel = Panel;
exports.Root = Root;
exports.Tab = Tab;
exports.Tabs = Tabs;
